/**
 * TP 充值记录 — legacy parity (日期 / 支付 / 金额 / 状态).
 */
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { rechargeAPI } from '../api/recharge';
import { EmptyState } from '../components/ui/EmptyState';
import { LoadingSkeleton } from '../components/ui/LoadingSkeleton';
import { QueryErrorNote } from '../components/ui/QueryErrorNote';
import { ThemedPageHeader } from '../components/ui/ThemedPageHeader';
import { THEMED_SURFACE_COLORS as C, ThemedPanel } from '../components/ui/ThemedSurface';

function fmtNum(v: string, digits = 2): string {
  const n = Number(v);
  if (!Number.isFinite(n)) return v;
  return n.toLocaleString(undefined, {
    minimumFractionDigits: digits > 0 ? 2 : 0,
    maximumFractionDigits: digits,
  });
}

function formatTime(value: string) {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())} ${pad(date.getHours())}:${pad(date.getMinutes())}:${pad(date.getSeconds())}`;
}

function statusColor(legacyStatus: string): string {
  if (legacyStatus === '已支付') return '#4ade80';
  if (legacyStatus === '未支付') return '#F74E5E';
  if (legacyStatus === '已拒绝') return '#F74E5E';
  return C.gold;
}

function RechargeOnchainRecords({ asset }: { asset: 'tp' | 'rp' | 'fc' }) {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const label = asset.toUpperCase();
  const i18n = asset === 'tp' ? 'rechargeTp' : asset === 'rp' ? 'rechargeRp' : 'rechargeFc';

  const { data, isLoading, isError, refetch } = useQuery({
    queryKey: ['recharge', `${asset}-records`],
    queryFn: async () => {
      if (asset === 'tp') {
        return rechargeAPI.getTpRecords({ page: 1, limit: 100 });
      }
      if (asset === 'fc') {
        const fc = await rechargeAPI.getFcRecords({ page: 1, limit: 100 });
        return {
          ...fc,
          rows: fc.rows.map((row) => ({ ...row, amount_tp: row.amount_fc })),
        };
      }
      const rp = await rechargeAPI.getRpRecords({ page: 1, limit: 100 });
      return {
        ...rp,
        rows: rp.rows.map((row) => ({ ...row, amount_tp: row.amount_rp })),
      };
    },
  });

  const rows = data?.rows ?? [];

  return (
    <div className="relative min-h-full" style={{ background: C.pageBg }}>
      <div className="relative mx-auto min-h-full w-full max-w-[430px]">
        <ThemedPageHeader
          title={t(`${i18n}.recordsTitle`, '{{asset}} 充值记录', { asset: label })}
          onBack={() => navigate(-1)}
          theme={C}
        />

        <div className="overflow-y-auto px-4 pt-[70px] pb-8">
          <ThemedPanel className="overflow-hidden" theme={C}>
            <div
              className="grid grid-cols-4 gap-1 border-b px-2 py-3 text-center text-xs font-semibold"
              style={{ borderColor: C.softBorder, color: C.text, background: C.inputBg }}
            >
              <div>{t('rechargeTp.colDate', '日期')}</div>
              <div>{t('rechargeTp.colPayment', '支付')}</div>
              <div>{t('rechargeTp.colAmount', '金额')}</div>
              <div>{t('rechargeTp.colStatus', '状态')}</div>
            </div>

            {isLoading ? (
              <div className="p-4">
                <LoadingSkeleton lines={4} />
              </div>
            ) : isError ? (
              <div className="p-4">
                <QueryErrorNote
                  message={t('rechargeTp.recordsLoadFailed', '加载充值记录失败')}
                  onRetry={() => void refetch()}
                />
              </div>
            ) : rows.length === 0 ? (
              <EmptyState title={t('rechargeTp.recordsEmpty', '暂无充值记录')} />
            ) : (
              rows.map((row) => (
                <div
                  key={row.id}
                  className="grid grid-cols-4 gap-1 border-b px-2 py-3 text-center text-[11px] leading-snug"
                  style={{ borderColor: C.softBorder, color: C.muted }}
                >
                  <div className="break-all">{formatTime(row.create_time)}</div>
                  <div className="font-mono">{fmtNum(row.payment_usdt)}</div>
                  <div className="font-mono">{fmtNum(row.amount_tp)}</div>
                  <div className="font-semibold" style={{ color: statusColor(row.legacy_status) }}>
                    {row.legacy_status}
                  </div>
                </div>
              ))
            )}
          </ThemedPanel>
        </div>
      </div>
    </div>
  );
}

export function RechargeTpRecords() {
  return <RechargeOnchainRecords asset="tp" />;
}

export function RechargeRpRecords() {
  return <RechargeOnchainRecords asset="rp" />;
}

export function RechargeFcRecords() {
  return <RechargeOnchainRecords asset="fc" />;
}
